import _ from "lodash";
import { MANAGE_MYP_CRITERIA_PHASE_SELECTION_PERMISSION } from "Constants/permissionConstants";
import ACLStorage from "lib/aclService";
import ACLStore from "lib/aclStore";
import update from "immutability-helper";
import { generateRnPContextsByLevel } from "Utils/RnPHelpers";
import { FINAL_SCORE, GRADE_SCALE, SCORE } from "Constants/stringConstants";
import { getIsGpaAndCreditsEnabled } from "./featureFlags";
import { GPA_ASSOCIATED_CRITERIA_TYPES_ELIGIBLE_FOR_AGGREGATION } from "./constants";
import { getValueBasedOnReportType, isPypVersion1 } from "./reportTypeHelpers";

export const getGradeScalesEnabledInPSAndAggregationSetting = ({
  templateSettings,
  aggregationTypeKeys,
  combinedScales,
}) => {
  const configKey = getValueBasedOnReportType({
    courseValue: "progressSummaryCourses",
    subjectValue: "progressSummarySubjects",
  });
  const progressSummaryGradeScales = _.keyBy(
    _.get(templateSettings, `gradeScaleConfiguration.${configKey}`, []),
    "id"
  );

  const combinedAggregateScales = _.uniq(
    _.flatMap(aggregationTypeKeys, type => {
      const aggregationTypeKey = getCriteriaEnabledInProgressSummaryAggregateTypeKey(
        { aggregationType: type }
      );
      return _.get(
        templateSettings,
        `aggregateGradeScaleConfig.${aggregationTypeKey}`,
        []
      );
    })
  );
  const aggregateGradeScales = _.filter(
    combinedAggregateScales,
    scale =>
      progressSummaryGradeScales[scale] &&
      progressSummaryGradeScales[scale]?.isEnabled
  );

  const result = _.filter(combinedScales, scale =>
    _.includes(aggregateGradeScales, scale?.id)
  );
  return result;
};

export const getCriteriaEnabledInProgressSummaryAggregateTypeKey = ({
  aggregationType,
}) =>
  aggregationType === "AVERAGE"
    ? getValueBasedOnReportType({
        subjectValue: "averageGradeScaleIds",
        courseValue: "averageGradeScaleIds",
        defaultValue: "averageCriteriaSetIds",
      })
    : getValueBasedOnReportType({
        subjectValue: "totalGradeScaleIds",
        courseValue: "totalGradeScaleIds",
        defaultValue: "totalCriteriaSetIds",
      });

export const getCriteriaEnabledInPSAndAggregationSetting = ({
  aggregationTypes,
  academicCriteriaSets,
  nodes,
}) => {
  const aggregateRatingNode = _.get(nodes, "ps=>aggregateScores", {});
  const isGpaAndCreditsFeatureEnabled = getIsGpaAndCreditsEnabled();

  const criteriaIdEnabledInAggregateSetting = isGpaAndCreditsFeatureEnabled
    ? _.uniq(
        _.flatMap(aggregationTypes, aggregationType => {
          const aggregateTypeKey = getCriteriaEnabledInProgressSummaryAggregateTypeKey(
            { aggregationType }
          );
          return _.get(
            aggregateRatingNode,
            ["criteriaEnabledInProgressSummary", aggregateTypeKey],
            []
          );
        })
      )
    : _.get(aggregateRatingNode, "criteriaEnabled", []);

  return _.filter(
    academicCriteriaSets,
    criteriaSet =>
      criteriaSet.showInProgressSummary &&
      _.includes(criteriaIdEnabledInAggregateSetting, criteriaSet.id)
  );
};

export const isConfigurationExistInCurrentTerm = ({
  scoreGradeAggregationInclusionMap,
  criteriaType,
  criteriaEnabledInPSAndAggregateSetting,
  academicTermIds,
  id,
}) => {
  // return false if the criteria set/grade scale is not enabled in progress summary and aggregate setting
  if (!_.some(criteriaEnabledInPSAndAggregateSetting, ["id", id])) {
    return false;
  }

  const gradingTypeToCheck = criteriaType === FINAL_SCORE ? SCORE : GRADE_SCALE;
  const scoreGradeAggregationConfigs = _.filter(
    scoreGradeAggregationInclusionMap,
    ({ gradingType }) => gradingType === gradingTypeToCheck
  );

  if (gradingTypeToCheck === SCORE) {
    // for final score only need to check for termId as scoreGradeAggregationConfigs with gradingType
    // as SCORE will only exist for final score
    return _.some(scoreGradeAggregationConfigs, config => {
      const academicTermId = _.has(config, "academicTermId")
        ? config.academicTermId
        : config?.academicTerm?.id;
      return _.includes(academicTermIds, academicTermId);
    });
  } else {
    // for grade scale, need to check for termId and gradeScaleId
    return _.some(scoreGradeAggregationConfigs, config => {
      const academicTermId = _.has(config, "academicTermId")
        ? config.academicTermId
        : config?.academicTerm?.id;
      return (
        _.includes(academicTermIds, academicTermId) &&
        id === config?.gradeScaleId
      );
    });
  }
};

export const isConfigurationExistForCriteriaAndTerm = ({
  scoreAggregationConfiguration,
  criteriaEnabledInPSAndAggregateSetting,
  aggregateParentCriteriaSetId,
  academicTermIds,
  aggregateAcademicCriteriaSetIds,
  subjectNodesIncludedInProgressSummary,
}) => {
  return _.some(scoreAggregationConfiguration, configuration => {
    const {
      academicCriteriaSetId,
      academicTermId,
      aggregateCriteriaSetIds = [],
    } = configuration;

    let isMatched = _.some(criteriaEnabledInPSAndAggregateSetting, [
      "id",
      academicCriteriaSetId,
    ]);

    if (aggregateParentCriteriaSetId) {
      isMatched =
        isMatched && aggregateParentCriteriaSetId == academicCriteriaSetId;
    }

    isMatched = isMatched && _.includes(academicTermIds, academicTermId);

    isMatched =
      isMatched &&
      _.size(
        _.intersection(aggregateCriteriaSetIds, aggregateAcademicCriteriaSetIds)
      ) > 0;

    isMatched = isMatched && _.size(subjectNodesIncludedInProgressSummary) > 0;

    return isMatched;
  });
};

export const getAggregateRatingExist = ({
  ratings,
  academicTermIds,
  aggregateAcademicCriteriaSetIds,
  aggregateParentCriteriaSetId,
  criteriaIdEnabledInPSAndAggregateSetting,
}) => {
  return _.some(
    ratings,
    rating =>
      _.includes(aggregateAcademicCriteriaSetIds, rating.academicCriteriaSet) &&
      _.includes(academicTermIds, rating.academicTermId) &&
      (aggregateParentCriteriaSetId
        ? rating.ibPYPElementId == aggregateParentCriteriaSetId
        : _.includes(
            criteriaIdEnabledInPSAndAggregateSetting,
            rating.ibPYPElementId
          ))
  );
};

export const getShouldHideAggregationRowOrColumnInReportSide = ({
  nodes,
  isScoreAndGradeAggregationEnabled,
  academicCriteriaSets,
  scoreAggregationConfiguration,
  academicTermIds,
  aggregateAcademicCriteriaSetIds,
  subjectNodesIncludedInProgressSummary,
  aggregateParentCriteriaSetId,
  isFreezed,
  ratings,
  isGPAEnabled,
  isCreditsEnabled,
  gpaAssociatedData,
  checkForShowExcludedSubjectHelpText = false,
  templateSettings,
  combinedScales,
  isGradeAggregationEnabled = false,
}) => {
  const entityId = getValueBasedOnReportType({
    courseValue: "ps=>courses",
    subjectValue: "ps=>subjects",
    defaultValue: "srLayout",
  });
  const isProgressSummarySubjectsDisplaySettingEnabled = getValueBasedOnReportType(
    {
      courseValue: _.get(nodes, `${entityId}.isEnabled`, false),
      subjectValue: _.get(nodes, `${entityId}.isEnabled`, false),
      defaultValue: _.includes(
        _.get(nodes, [[entityId], "configValue", "displayOn"], []),
        "progressSummary"
      ),
    }
  );

  const gradePointAverageCriteriaSet = _.find(academicCriteriaSets, {
    criteriaType: "GRADE_POINT_AVERAGE",
  });
  const isGPAEnabledInTemplateAndAcademicSetup =
    _.get(gradePointAverageCriteriaSet, "isRatingEnabled", false) &&
    isGPAEnabled;

  const aggregateAcademicCriteriaSetTypes = _.map(
    aggregateAcademicCriteriaSetIds,
    setId => {
      return _.get(_.find(academicCriteriaSets, { id: setId }), "subType");
    }
  );

  // Get the criteria sets which are enabled in progress summary and aggregate setting
  const criteriaEnabledInPSAndAggregateSetting = getValueBasedOnReportType()
    ? getGradeScalesEnabledInPSAndAggregationSetting({
        templateSettings,
        aggregationTypeKeys: aggregateAcademicCriteriaSetTypes,
        combinedScales,
      })
    : getCriteriaEnabledInPSAndAggregationSetting({
        academicCriteriaSets,
        nodes,
        aggregationTypes: aggregateAcademicCriteriaSetTypes,
      });

  const criteriaVisibilityMap = {
    FINAL_SCORE: isScoreAndGradeAggregationEnabled,
    FINAL_GRADE: isScoreAndGradeAggregationEnabled,
    CREDIT: isCreditsEnabled,
    QUALITY_POINT: isGPAEnabled && isCreditsEnabled,
  };

  // Filter enabled criteria sets based on the whether their respective settings in academic setup are enabled
  const filteredCriteriaEnabledInPSAndAggregateSetting = _.filter(
    criteriaEnabledInPSAndAggregateSetting,
    criteriaSet => {
      const criteriaSetType = _.get(criteriaSet, "criteriaType", "");
      return criteriaSetType
        ? criteriaVisibilityMap[criteriaSetType]
        : isGradeAggregationEnabled;
    }
  );

  // If aggregateParentCriteriaSetId (criteria set id being aggregated) is passed, check whether this criteria set
  // is enabled in both the progress summary and aggregate settings.
  const isAggregateParentCriteriaSetEnabledInPSAndAggregateSetting = aggregateParentCriteriaSetId
    ? _.some(filteredCriteriaEnabledInPSAndAggregateSetting, [
        "id",
        aggregateParentCriteriaSetId,
      ])
    : true;

  const aggregateParentCriteriaSet = _.find(academicCriteriaSets, {
    id: aggregateParentCriteriaSetId,
  });

  /*
    GPA-associated criteria sets do not support aggregation configuration from the academic setup.
    Their visibility can only be controlled from the template, not from the academic setup.

    As a result, we skip configuration validation for GPA-associated criteria sets.

    Since this function is used for both row and column visibility checks, whether to skip configuration validation depends on the following conditions:
      1) For rows: If "any" GPA-associated criteria set is enabled in the progress summary and aggregation settings.
         we skip the configuration check, as the row must be displayed even if a single criteria set rating needs to be shown. [First check below]
      2) For columns: If the column corresponds to a GPA-associated criteria set rating,
         we skip the configuration check. [Second check below]
*/
  const shouldSkipAggregationConfigurationMatching =
    !_.isEmpty(
      _.intersection(
        _.map(filteredCriteriaEnabledInPSAndAggregateSetting, "criteriaType"),
        GPA_ASSOCIATED_CRITERIA_TYPES_ELIGIBLE_FOR_AGGREGATION
      )
    ) &&
    (aggregateParentCriteriaSet
      ? _.includes(
          GPA_ASSOCIATED_CRITERIA_TYPES_ELIGIBLE_FOR_AGGREGATION,
          _.get(aggregateParentCriteriaSet, "criteriaType")
        )
      : true);

  const isConfigurationExistForAnyCriteriaAndTerm =
    shouldSkipAggregationConfigurationMatching || getValueBasedOnReportType()
      ? true
      : isConfigurationExistForCriteriaAndTerm({
          scoreAggregationConfiguration,
          criteriaEnabledInPSAndAggregateSetting: filteredCriteriaEnabledInPSAndAggregateSetting,
          aggregateParentCriteriaSetId,
          academicTermIds,
          aggregateAcademicCriteriaSetIds,
          subjectNodesIncludedInProgressSummary,
        });

  const gpaAssociatedAggregateRatings = _.get(
    gpaAssociatedData,
    "aggregateRatings",
    []
  );

  const otherCriterionsAggregateRatings = _.filter(ratings, rating => {
    const matchingCriteriaSet = _.find(academicCriteriaSets, {
      id: rating.academicCriteriaSet,
    });
    return _.isEqual(_.get(matchingCriteriaSet, "criteriaType"), "AGGREGATE");
  });

  const aggregateRatings = getValueBasedOnReportType()
    ? gpaAssociatedAggregateRatings
    : _.concat(gpaAssociatedAggregateRatings, otherCriterionsAggregateRatings);

  const isLockedReportAndAggregateRatingExist = isFreezed
    ? getAggregateRatingExist({
        ratings: aggregateRatings,
        aggregateParentCriteriaSetId,
        criteriaIdEnabledInPSAndAggregateSetting: _.map(
          filteredCriteriaEnabledInPSAndAggregateSetting,
          "id"
        ),
        aggregateAcademicCriteriaSetIds,
        academicTermIds,
      })
    : true;

  const conditionForShowExcludedSubjectHelpText =
    isGPAEnabledInTemplateAndAcademicSetup ||
    (isFreezed && isLockedReportAndAggregateRatingExist) ||
    (isConfigurationExistForAnyCriteriaAndTerm &&
      isScoreAndGradeAggregationEnabled);

  return !(
    isProgressSummarySubjectsDisplaySettingEnabled &&
    isAggregateParentCriteriaSetEnabledInPSAndAggregateSetting &&
    _.size(subjectNodesIncludedInProgressSummary) > 0 &&
    (checkForShowExcludedSubjectHelpText
      ? conditionForShowExcludedSubjectHelpText
      : isConfigurationExistForAnyCriteriaAndTerm &&
        isLockedReportAndAggregateRatingExist)
  );
};

export const getIsGpaRatingExist = ({ gpaRatings, academicTermIds }) => {
  return _.some(gpaRatings, rating =>
    _.includes(academicTermIds, rating.academicTermId)
  );
};

export const getShouldHideGpaRowInTemplate = ({
  nodes,
  gpaCriteriaSet,
  templateSettings,
}) => {
  if (!gpaCriteriaSet) {
    return true;
  }
  const aggregateAverageGradeScaleConfig = _.get(
    templateSettings,
    "aggregateGradeScaleConfig.averageGradeScaleIds",
    []
  );
  const isGpaCriteriaSetEnabled = getValueBasedOnReportType()
    ? _.includes(aggregateAverageGradeScaleConfig, gpaCriteriaSet?.id)
    : _.get(gpaCriteriaSet, "isRatingEnabled", false);

  const entityId = getValueBasedOnReportType({
    courseValue: "ps=>courses",
    subjectValue: "ps=>subjects",
    defaultValue: "srLayout",
  });
  const isProgressSummarySubjectsDisplaySettingEnabled = getValueBasedOnReportType(
    {
      courseValue: _.get(nodes, `${entityId}.isEnabled`, false),
      subjectValue: _.get(nodes, `${entityId}.isEnabled`, false),
      defaultValue: _.includes(
        _.get(nodes, [[entityId], "configValue", "displayOn"], []),
        "progressSummary"
      ),
    }
  );

  return (
    !isProgressSummarySubjectsDisplaySettingEnabled || !isGpaCriteriaSetEnabled
  );
};

export const getShouldHideGpaRowInReportSide = ({
  nodes,
  gpaRatings,
  isFreezed,
  academicTermIds,
  gpaCriteriaSet,
  templateSettings,
}) => {
  const entityId = getValueBasedOnReportType({
    courseValue: "ps=>courses",
    subjectValue: "ps=>subjects",
    defaultValue: "srLayout",
  });
  const isProgressSummarySubjectsDisplaySettingEnabled = getValueBasedOnReportType(
    {
      courseValue: _.get(nodes, `${entityId}.isEnabled`, false),
      subjectValue: _.get(nodes, `${entityId}.isEnabled`, false),
      defaultValue: _.includes(
        _.get(nodes, [[entityId], "configValue", "displayOn"], []),
        "progressSummary"
      ),
    }
  );

  const aggregateAverageGradeScaleConfig = _.get(
    templateSettings,
    "aggregateGradeScaleConfig.averageGradeScaleIds",
    []
  );
  const isGpaCriteriaSetEnabled = getValueBasedOnReportType()
    ? _.includes(aggregateAverageGradeScaleConfig, gpaCriteriaSet?.id)
    : _.get(gpaCriteriaSet, "isRatingEnabled", false);

  const isLockedReportAndGpaRatingExist = isFreezed
    ? getIsGpaRatingExist({ gpaRatings, academicTermIds })
    : true;

  return !(
    isProgressSummarySubjectsDisplaySettingEnabled &&
    isGpaCriteriaSetEnabled &&
    isLockedReportAndGpaRatingExist
  );
};

export const getAggregateAcademicCriteriaBasedOnNode = ({
  academicCriteriaSets,
  uid,
}) => {
  const nodeCriteriaMap = {
    AGGREGATED_SCORES_TOTAL: {
      criteriaType: "AGGREGATE",
      subType: "SUM",
    },
    AGGREGATED_SCORES_AVERAGE: {
      criteriaType: "AGGREGATE",
      subType: "AVERAGE",
    },
  };

  const { criteriaType, subType } = nodeCriteriaMap[uid];

  return _.find(academicCriteriaSets, { criteriaType, subType }) || {};
};

export const shouldHideAggregationRowOrColumnInTemplate = ({
  aggregationType,
  isGPAEnabled,
  isCreditsEnabled,
  isScoreAndGradeAggregationEnabled,
  nodes,
  academicCriteriaSets,
  isGradeAggregationEnabled,
}) => {
  const entityId = getValueBasedOnReportType({
    courseValue: "ps=>courses",
    subjectValue: "ps=>subjects",
    defaultValue: "srLayout",
  });
  const isProgressSummarySubjectsDisplaySettingEnabled = getValueBasedOnReportType(
    {
      courseValue: _.get(nodes, `${entityId}.isEnabled`, false),
      subjectValue: _.get(nodes, `${entityId}.isEnabled`, false),
      defaultValue: _.includes(
        _.get(nodes, [[entityId], "configValue", "displayOn"], []),
        "progressSummary"
      ),
    }
  );

  // Get the criteria sets which are enabled in progress summary and aggregate setting
  const criteriaEnabledInPSAndAggregateSetting = getValueBasedOnReportType()
    ? academicCriteriaSets[aggregationType]
    : getCriteriaEnabledInPSAndAggregationSetting({
        aggregationTypes: [aggregationType],
        academicCriteriaSets,
        nodes,
      });

  const criteriaVisibilityMap = {
    FINAL_SCORE: isScoreAndGradeAggregationEnabled,
    FINAL_GRADE: isScoreAndGradeAggregationEnabled,
    CREDIT: isCreditsEnabled,
    QUALITY_POINT: isGPAEnabled && isCreditsEnabled,
  };

  // Filter enabled criteria sets based on the whether their respective settings in academic setup are enabled
  const filteredCriteriaEnabledInPSAndAggregateSetting = _.filter(
    criteriaEnabledInPSAndAggregateSetting,
    criteriaSet => {
      const criteriaSetType = _.get(criteriaSet, "criteriaType", "");
      return criteriaSetType
        ? criteriaVisibilityMap[criteriaSetType]
        : isGradeAggregationEnabled;
    }
  );

  const isAnyCriteriaSetEnabledForAggregation =
    _.size(filteredCriteriaEnabledInPSAndAggregateSetting) > 0;

  return !(
    isProgressSummarySubjectsDisplaySettingEnabled &&
    isAnyCriteriaSetEnabledForAggregation
  );
};

// Pdf file name format

export const getPsNodeByCurriculum = ({ curriculumType }) => {
  if (isPypVersion1({ curriculumType })) {
    return "psBenchmarksSummary";
  } else {
    return getValueBasedOnReportType({
      courseValue: "ps=>courses",
      subjectValue: "ps=>subjects",
      defaultValue: "ps=>subjects",
    });
  }
};

export const canManageMYPCriteriaConfigurationForCourse = ({
  courseId,
  gradeId,
  curriculumProgramId,
}) => {
  const contexts = generateRnPContextsByLevel({
    level: "COURSE",
    curriculumProgramId,
    courseId,
    gradeId,
  });

  const context = _.first(contexts);

  const isTeacherAbleToManageCriteria = ACLStorage.getPermissionValueByContext(
    MANAGE_MYP_CRITERIA_PHASE_SELECTION_PERMISSION,
    context
  );

  return isTeacherAbleToManageCriteria;
};

const nodesWithIsVisibleFalseForOldCoversheetTemplate = [
  "schoolAddress",
  "prCreationDate",
  "dateOfBirth",
  "studentHomeroomAdvisor",
  "gender",
  "address",
  "nationality",
  "enrollmentId",
];

export const getUpdatedNodesWithIsVisibleForCoversheetTemplateChange = ({
  nodes,
  selectedModalValue,
}) => {
  const showNodeValue = selectedModalValue == "old-template" ? false : true;
  const updatedNodes = {};
  const studentAdditionalDetailsChildren = _.get(
    nodes,
    "studentAdditionalDetails.children",
    []
  );
  const nodesToMakeVisibilityFalse = !_.isEmpty(
    studentAdditionalDetailsChildren
  )
    ? studentAdditionalDetailsChildren
    : nodesWithIsVisibleFalseForOldCoversheetTemplate;

  let updatedNodesToMakeVisibilityFalse = [
    ...nodesToMakeVisibilityFalse,
    "studentAdditionalDetails",
  ];

  if (ACLStore.can("FeatureFlag:ShowStudentUIDInOldProgressReportCoverSheet")) {
    updatedNodesToMakeVisibilityFalse = _.filter(
      updatedNodesToMakeVisibilityFalse,
      nodeId =>
        !_.includes(["enrollmentId", "studentAdditionalDetails"], nodeId)
    );
  }

  _.forEach(nodes, node => {
    const { id } = node;
    if (_.includes(updatedNodesToMakeVisibilityFalse, id)) {
      const updatedNode = update(node, {
        create: {
          isVisible: { $set: showNodeValue },
        },
      });
      updatedNodes[id] = updatedNode;
    }
  });

  updatedNodes["studentInformation"] = update(
    _.get(nodes, "studentInformation", {}),
    {
      create: {
        showSubText: { $set: showNodeValue },
      },
    }
  );
  return updatedNodes;
};
